import { useMemo } from 'react';
import { EventPageType, EventPageFragment } from '@graphql/generated';
import { RoutesPaths } from '@shared/core';
import useQueryString from '@shared/utils/hooks/useQueryString';
import { getMigratedPageSlug } from '@shared/utils/migratedCustomPages';
import { useMigratedCustomPageFeatureFlags } from '@shared/hooks/useMigratedCustomPageFeatureFlags/useMigratedCustomPageFeatureFlags';

const SUPPORTED_PAGES = [
  EventPageType.welcome,
  EventPageType.story,
  EventPageType.vip,
  EventPageType.faq,
  EventPageType.schedule,
  EventPageType.travel,
  EventPageType.moments,
  EventPageType.registry,
  EventPageType.rsvp,
  EventPageType.tidbits,
  EventPageType.custom,
] as const;

const supportedPagesSet = new Set<EventPageType>(SUPPORTED_PAGES);

export type SupportedEventPages = (typeof SUPPORTED_PAGES)[number];

export type EventPageRoutePaths = Partial<RoutesPaths<SupportedEventPages>>;

export const useEventPageRouting = (eventHandle: string, pages: EventPageFragment[]): EventPageRoutePaths => {
  const { isMigratedCustomPageEnabled } = useMigratedCustomPageFeatureFlags();

  return useMemo(() => {
    if (!eventHandle || !eventHandle.trim().length) {
      return {};
    }
    return pages.reduce((acc, page) => {
      if (supportedPagesSet.has(page.type)) {
        // For migrated pages (like VIP) when feature flag is ON, use the canonical type slug (e.g., 'vip')
        // instead of the page's pageSlug (e.g., 'weddingparty')
        const migratedSlug = getMigratedPageSlug(page.type);
        const slug = (migratedSlug && isMigratedCustomPageEnabled(migratedSlug) ? migratedSlug : undefined) || page.pageSlug;
        acc[page.type as SupportedEventPages] = {
          path: `/${eventHandle}/${slug}`,
          goToPath: () => slug,
        };
      }
      return acc;
    }, {} as EventPageRoutePaths);
  }, [eventHandle, pages, isMigratedCustomPageEnabled]);
};

export const ACCOMMODATIONS_SLUG = 'accommodations';
export const CUSTOM_PAGE_PATH = 'page';

export const useEventPageRoute = (eventHandle: string) => {
  const { isMigratedCustomPageEnabled } = useMigratedCustomPageFeatureFlags();

  const queryString = useQueryString();
  const eventPageRoute = {
    path: `/:eventHandle`,
    goToPath: (type: EventPageType, pageSlug: string) => {
      // Determine if this should use the /page/ prefix route
      const isAccommodations = pageSlug === ACCOMMODATIONS_SLUG;
      const isMigratedPageWithFlagEnabled = isMigratedCustomPageEnabled(pageSlug);
      const useCustomPath = type === EventPageType.custom && !isAccommodations && !isMigratedPageWithFlagEnabled;

      let url = useCustomPath ? `/${eventHandle}/${CUSTOM_PAGE_PATH}/${pageSlug}` : `/${eventHandle}/${pageSlug}`;

      if (queryString) {
        url += `${queryString}`;
      }

      return url;
    },
  };
  return eventPageRoute;
};
